import { authClient } from './authClient'
import { useAuthStore } from './authStore'

let refreshInFlight: Promise<boolean> | null = null

async function tryRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const r = await authClient.refresh()
        useAuthStore.getState().setAuthenticated({
          accessToken: r.access_token,
          expiresIn: r.expires_in,
          user: r.user,
        })
        return true
      } catch {
        useAuthStore.getState().setAnonymous('session_expired')
        return false
      } finally {
        refreshInFlight = null
      }
    })()
  }
  return refreshInFlight
}

function withAuth(init: RequestInit, token: string | null): RequestInit {
  const headers = new Headers(init.headers ?? {})
  if (token) headers.set('Authorization', `Bearer ${token}`)
  return { credentials: 'include', ...init, headers }
}

/**
 * fetch() wrapper that attaches the current access token, attempts a single
 * silent refresh on 401, then retries. On a second 401 the store is flipped to
 * anonymous and the AuthGate will render the login screen.
 */
export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const token = useAuthStore.getState().accessToken
  let res = await fetch(input, withAuth(init, token))
  if (res.status !== 401) return res

  const refreshed = await tryRefresh()
  if (!refreshed) return res

  const nextToken = useAuthStore.getState().accessToken
  res = await fetch(input, withAuth(init, nextToken))
  if (res.status === 401) {
    useAuthStore.getState().setAnonymous('session_expired')
  }
  return res
}
